import { Component, NgZone, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { MenuController, Platform, ToastController } from '@ionic/angular/lazy';

import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

import { Storage } from '@ionic/storage-angular';

import { UserData } from './providers/user-data';
import {UserService} from './services/user.service';
import {Account} from './api/models/account';
import { PwaInstallService } from './services/pwa-install.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit {
  appPages = [
    {
      title: 'Veranstaltungen',
      url: '/app/tabs/event',
      icon: 'calendar'
    },
    {
      title: 'Workshops',
      url: '/app/tabs/schedule',
      icon: 'calendar'
    },
    {
      title: 'Referenten',
      url: '/app/tabs/speakers',
      icon: 'people'
    }

  ];
  loggedIn = false;
  dark = false;
  account: Account;
  canInstall = false;
  showIosInstallHint = false;

  constructor(
    private menu: MenuController,
    private platform: Platform,
    private router: Router,
    private zone: NgZone,
    private storage: Storage,
    private userData: UserData,
    private user: UserService,
    private swUpdate: SwUpdate,
    private toastCtrl: ToastController,
    private pwaInstall: PwaInstallService,
  ) {
    this.initializeApp();
  }

  async ngOnInit() {
    this.checkLoginStatus();
    this.listenForLoginEvents();

    // Only offer the install entry in the browser; inside the native shell
    // (or an already installed PWA) there is nothing to install.
    if (!Capacitor.isNativePlatform()) {
      this.pwaInstall.installable$.subscribe(x => this.canInstall = x);
      this.showIosInstallHint = this.pwaInstall.needsIosInstructions;
    }

    this.user.getAccount().subscribe(x => this.account = x);

    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(async () => {
      const toast = await this.toastCtrl.create({
        message: 'Update verfügbar!',
        position: 'bottom',
        buttons: [
          {
            role: 'cancel',
            text: 'Reload'
          }
        ]
      });

      await toast.present();

      toast
        .onDidDismiss()
        .then(() => this.swUpdate.activateUpdate())
        .then(() => window.location.reload());
    });
  }

  initializeApp() {
    this.platform.ready().then(async () => {
      // The status bar / splash screen plugins are native-only no-ops on web.
      if (Capacitor.isNativePlatform()) {
        await StatusBar.setStyle({ style: Style.Default });
        await SplashScreen.hide();
      }
    });

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        // Example url: https://beerswift.app/tabs/tab2
        // slug = /tabs/tab2
        const slug = event.url.split('.app').pop();
        if (slug) {
          this.router.navigateByUrl(slug);
        }
        // If no match, do nothing - let regular routing
        // logic take over
      });
    });
  }


  checkLoginStatus() {
    this.user.isLoggedIn$().subscribe(
        x => this.updateLoggedInStatus(x)
    );
  }

  updateLoggedInStatus(loggedIn: boolean) {
    setTimeout(() => {
      this.loggedIn = loggedIn;
    }, 300);
  }

  listenForLoginEvents() {
    window.addEventListener('user:login', () => {
      this.updateLoggedInStatus(true);
    });

    window.addEventListener('user:signup', () => {
      this.updateLoggedInStatus(true);
    });

    window.addEventListener('user:logout', () => {
      this.updateLoggedInStatus(false);
    });
  }

  logout() {
    this.user.logOut();
  }

  async installApp() {
    const outcome = await this.pwaInstall.install();
    if (outcome === 'unavailable' && this.showIosInstallHint) {
      const toast = await this.toastCtrl.create({
        message: 'Zum Installieren: Teilen-Symbol antippen und "Zum Home-Bildschirm".',
        position: 'bottom',
        duration: 6000,
        buttons: [{ role: 'cancel', text: 'OK' }]
      });
      await toast.present();
    }
  }

}
